<template>
  <div id="app">
    <!-- 导航栏 -->
    <nav class="navbar">
      <div class="navbar-container">
        <div class="navbar-brand">
          <i class="fas fa-dice-d20" style="color: #818cf8;"></i>
          <span>哈希量化</span>
        </div>
        <div class="navbar-menu">
          <router-link to="/" class="navbar-item" :class="{ active: $route.path === '/' }">
            <i class="fas fa-home"></i> 首页
          </router-link>
          <router-link to="/history" class="navbar-item" :class="{ active: $route.path === '/history' }">
            <i class="fas fa-history"></i> 历史记录
          </router-link>
          <router-link to="/settings" class="navbar-item" :class="{ active: $route.path === '/settings' }">
            <i class="fas fa-cog"></i> 设置
          </router-link>
        </div>
      </div>
    </nav>

    <!-- 路由视图 -->
    <router-view v-slot="{ Component }">
      <transition name="fade" mode="out-in">
        <component :is="Component" />
      </transition>
    </router-view>
  </div>
</template>

<script>
export default {
  name: 'App'
}
</script>

<style lang="scss">
/* 变量定义 */
$primary-color: #818cf8;
$secondary-color: #4f46e5;
$bg-color: linear-gradient(145deg, #0b0e1a 0%, #1a1f2f 100%);
$card-bg: rgba(18, 22, 40, 0.8);
$text-color: #e6e9ff;
$text-muted: #8f9bb5;
$border-radius: 32px;

/* 全局样式 */
* {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
  font-family: 'Segoe UI', Roboto, 'Helvetica Neue', sans-serif;
}

body {
  background: $bg-color;
  min-height: 100vh;
  padding: 20px;
}

#app {
  max-width: 1200px;
  width: 100%;
  margin: 0 auto;
}

/* 导航栏样式 */
.navbar {
  background: rgba(18, 22, 40, 0.95);
  backdrop-filter: blur(12px);
  border-radius: 24px;
  margin-bottom: 20px;
  padding: 15px 25px;
  border: 1px solid rgba(120, 130, 220, 0.3);
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.5);

  .navbar-container {
    display: flex;
    justify-content: space-between;
    align-items: center;
  }

  .navbar-brand {
    display: flex;
    align-items: center;
    gap: 10px;
    font-size: 1.2rem;
    font-weight: 600;
    background: linear-gradient(135deg, #a5b4fc, $primary-color);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;

    i {
      font-size: 1.5rem;
    }
  }

  .navbar-menu {
    display: flex;
    gap: 15px;

    .navbar-item {
      color: #b9c2e6;
      text-decoration: none;
      padding: 8px 16px;
      border-radius: 30px;
      display: flex;
      align-items: center;
      gap: 8px;
      transition: all 0.2s;
      border: 1px solid transparent;

      &:hover {
        background: #2a3457;
        color: white;
        border-color: $primary-color;
      }

      &.active {
        background: linear-gradient(145deg, $secondary-color, #6366f1);
        color: white;
        border-color: #a78bfa;
        box-shadow: 0 0 15px rgba(79, 70, 229, 0.5);
      }
    }
  }
}

/* 路由过渡动画 */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

/* 通用样式 */
.dashboard {
  background: $card-bg;
  backdrop-filter: blur(12px);
  border-radius: $border-radius;
  padding: 28px;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.6), 0 0 0 1px rgba(66, 153, 225, 0.2) inset;
  border: 1px solid rgba(120, 130, 220, 0.3);
  color: $text-color;

  .header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 28px;
    flex-wrap: wrap;
    gap: 15px;

    .title {
      h1 {
        font-size: 1.9rem;
        font-weight: 600;
        background: linear-gradient(135deg, #a5b4fc, $primary-color);
        -webkit-background-clip: text;
        -webkit-text-fill-color: transparent;
        letter-spacing: 1px;
      }

      p {
        color: $text-muted;
        font-size: 0.9rem;
        margin-top: 5px;
      }
    }

    .badge {
      background: #262e4f;
      padding: 10px 20px;
      border-radius: 40px;
      border: 1px solid #3f4a7a;
      font-weight: 500;
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.4);

      i {
        color: #fbbf24;
        margin-right: 8px;
      }
    }
  }

  .info-panel {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    gap: 18px;
    margin-bottom: 32px;

    .info-card {
      background: #1e2340;
      border-radius: 24px;
      padding: 20px 18px;
      border-left: 4px solid;
      transition: 0.2s;
      box-shadow: 0 8px 20px rgba(0, 0, 0, 0.5);

      &.block { border-left-color: #60a5fa; }
      &.hash { border-left-color: #c084fc; }
      &.size { border-left-color: #34d399; }
      &.parity { border-left-color: #f87171; }

      .info-label {
        font-size: 0.8rem;
        text-transform: uppercase;
        letter-spacing: 1px;
        color: #9aa4c9;
        margin-bottom: 8px;
      }

      .info-value {
        font-size: 1.5rem;
        font-weight: 600;
        word-break: break-all;
        font-family: 'Courier New', monospace;
      }

      .hash-value {
        font-size: 1.1rem;
        background: #0f1222;
        padding: 8px 12px;
        border-radius: 18px;
        border: 1px solid #334155;
      }
    }
  }

  .last-digit {
    display: inline-block;
    background: #2d3748;
    color: #fbbf24;
    font-weight: 700;
    padding: 2px 10px;
    border-radius: 30px;
    margin-left: 10px;
    font-size: 1.2rem;
  }

  .result-badge {
    display: inline-block;
    padding: 6px 18px;
    border-radius: 30px;
    font-weight: 700;
    font-size: 1.3rem;
    background: rgba(255,255,255,0.05);
    border: 1px solid;
    margin-left: 8px;

    &.size-badge { border-color: #34d399; color: #6ee7b7; }
    &.parity-badge { border-color: #f87171; color: #fca5a5; }
  }

  .trade-panel {
    background: #151b32;
    border-radius: 28px;
    padding: 24px;
    margin-bottom: 30px;

    .balance-area {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 20px;
      flex-wrap: wrap;
      gap: 15px;

      .balance-box {
        background: #0f1424;
        padding: 12px 22px;
        border-radius: 60px;
        border: 1px solid #3c476b;

        .balance-amount {
          font-size: 2rem;
          font-weight: 700;
          color: #fcd34d;
          margin-left: 8px;
        }
      }

      .reset-btn {
        background: none;
        border: 1px solid #5f6b96;
        color: #b9c2e6;
        padding: 8px 18px;
        border-radius: 30px;
        cursor: pointer;
        transition: 0.2s;
        font-size: 0.9rem;

        &:hover {
          background: #2a3457;
          color: white;
          border-color: $primary-color;
        }
      }
    }

    .bet-controls {
      display: flex;
      flex-wrap: wrap;
      gap: 20px;
      align-items: flex-end;

      .input-group {
        flex: 2 1 200px;

        label {
          display: block;
          font-size: 0.8rem;
          color: #a0abcf;
          margin-bottom: 6px;
          letter-spacing: 0.5px;
        }

        .amount-input {
          background: #0c1020;
          border: 1px solid #39456b;
          border-radius: 40px;
          padding: 12px 20px;
          width: 100%;
          color: white;
          font-weight: 600;
          font-size: 1.1rem;
          outline: none;

          &:focus {
            border-color: $primary-color;
          }
        }
      }

      .bet-options {
        flex: 3 1 300px;
        display: flex;
        gap: 10px;
        flex-wrap: wrap;

        .bet-btn {
          background: #1f2645;
          border: 1px solid #3c4a78;
          color: #cbd5e1;
          font-size: 1.3rem;
          font-weight: 700;
          padding: 12px 0;
          border-radius: 50px;
          flex: 1 1 70px;
          min-width: 70px;
          cursor: pointer;
          transition: all 0.15s;
          box-shadow: 0 4px 8px rgba(0,0,0,0.3);

          &.active {
            background: linear-gradient(145deg, $secondary-color, #6366f1);
            border-color: #a78bfa;
            color: white;
            transform: scale(1.02);
            box-shadow: 0 0 15px #6366f1;

            &.big { background: #ef4444; border-color: #f87171; }
            &.small { background: #3b82f6; border-color: #60a5fa; }
            &.odd { background: #8b5cf6; border-color: #c084fc; }
            &.even { background: #10b981; border-color: #34d399; }
          }
        }
      }

      .action-btns {
        flex: 1 1 200px;
        display: flex;
        gap: 12px;

        .action-btn {
          background: #283153;
          border: none;
          color: white;
          padding: 12px 20px;
          border-radius: 40px;
          font-weight: 600;
          font-size: 1rem;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          flex: 1;
          transition: 0.2s;
          border: 1px solid #475688;

          &.primary {
            background: $secondary-color;
            border-color: $primary-color;

            &:hover {
              background: #6366f1;
              box-shadow: 0 0 15px $secondary-color;
            }
          }

          &.warning {
            background: #b45309;
            border-color: #f59e0b;

            &:hover {
              background: #d97706;
            }
          }

          &:disabled {
            opacity: 0.4;
            cursor: not-allowed;
          }
        }
      }
    }

    .error-banner {
      background: #7f1d1d;
      color: #fecaca;
      padding: 12px 20px;
      border-radius: 30px;
      margin-top: 15px;
      border: 1px solid #f87171;
    }
  }

  .history-section {
    background: #151b32;
    border-radius: 24px;
    padding: 20px;

    .section-title {
      font-size: 1.3rem;
      margin-bottom: 16px;
      display: flex;
      align-items: center;
      gap: 10px;
      color: #b3c0f0;

      .stats {
        display: flex;
        gap: 25px;
        margin-left: auto;
        font-size: 0.9rem;
        background: #202842;
        padding: 6px 16px;
        border-radius: 30px;
      }
    }

    .history-list {
      max-height: 300px;
      overflow-y: auto;
      border-radius: 20px;
      background: #0f1422;
      padding: 5px;

      .history-item {
        display: flex;
        align-items: center;
        justify-content: space-between;
        background: #1a2140;
        margin: 8px 0;
        padding: 12px 18px;
        border-radius: 40px;
        border-left: 5px solid transparent;
        font-size: 0.95rem;

        &.win {
          border-left-color: #10b981;
          background: #1a2f3a;
        }

        &.loss {
          border-left-color: #ef4444;
          background: #2a2438;
        }

        .history-block {
          font-family: monospace;
          background: #0b0f1c;
          padding: 4px 10px;
          border-radius: 20px;
          color: #9bb0f0;
        }

        .history-bet {
          font-weight: 700;
          padding: 4px 12px;
          border-radius: 30px;
          background: #252e50;
        }

        .history-profit {
          font-weight: 700;

          &.profit-positive { color: #6ee7b7; }
          &.profit-negative { color: #fca5a5; }
        }
      }
    }
  }

  .loading-spinner {
    display: inline-block;
    width: 18px;
    height: 18px;
    border: 3px solid rgba(255,255,255,0.2);
    border-radius: 50%;
    border-top-color: white;
    animation: spin 0.8s linear infinite;
  }

  .footer-note {
    margin-top: 20px;
    text-align: center;
    color: #5f6b96;
    font-size: 0.8rem;
  }
}

/* 动画 */
@keyframes spin {
  to { transform: rotate(360deg); }
}

/* 响应式设计 */
@media (max-width: 768px) {
  .navbar {
    .navbar-container {
      flex-direction: column;
      gap: 15px;
    }

    .navbar-menu {
      width: 100%;
      justify-content: space-around;
    }
  }

  .dashboard {
    .bet-controls {
      flex-direction: column;
      align-items: stretch;

      .bet-options {
        flex-direction: row;
      }
    }
  }
}
</style>